import { DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AdvanceFilterParamType, IAdvancedFilterListItem, IAdvanceFilter } from 'app/main/shared/advanced-filters/components/advance-filter';
import { debounceTime, filter, Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { IAppliedFiltersUpdaterToken } from '../../../shared/advanced-filters/applied-filters-updater';
import { IFilterChooserToken } from '../../../shared/advanced-filters/filter-chooser';
import { SaveFilterComponent } from '../../../shared/save-listing-filters/components/save-filter/save-filter.component';
import { IGetCategoriesItemDto } from '../../categories.dto';
import { IDocumentTypesDto, IUserGroupFilter, OwnerFilterType } from '../dto/document-listing.dto';
import { DocumentListingFacade } from '../store/document-listing.facade';
import { DocumentViewVariantEnum, IDocumentFilterState } from '../store/document-listing.reducers';
import { DocumentFilterChooser } from './document-filter-chooser';
import { DocumentsAppliedFiltersUpdaterService } from './documents-applied-filters-updater.service';
import { DocumentTypeSectionEnum } from '../../settings/documenttype/document-type-settings-modal/default-constants/document-type-settings.constants';
@Component({
    selector: 'app-document-main-sidebar',
    templateUrl: './document-main-sidebar.component.html',
    styleUrls: ['./document-main-sidebar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: IFilterChooserToken,
            useClass: DocumentFilterChooser
        },
        {
            provide: IAppliedFiltersUpdaterToken,
            useExisting: DocumentsAppliedFiltersUpdaterService
        }
    ]

})
export class DocumentMainSidebarComponent implements OnInit, OnDestroy {

    @Input() activeView: string = 'table_view';
    @Output() showNestedChartViews: EventEmitter<{ view: DocumentViewVariantEnum }> = new EventEmitter();


    public documentFilters$: Observable<IDocumentFilterState> = this._documentListingFacade.filterBy$;
    public documentTypes$: Observable<IDocumentTypesDto[]> = this._documentListingFacade.documentTypes$;
    public categories$: Observable<IGetCategoriesItemDto[]> = this._documentListingFacade.categories$;
    public ownerFilterDDL$: Observable<IUserGroupFilter[]> = this._documentListingFacade.ownerFilterDDL$;
    public hasAnyFiltersApplied$: Observable<boolean> = this._documentListingFacade.hasAnyFiltersApplied$;
    public dateFormat = 'YYYY-MM-dd';
    public todayDate = new Date();
    public documentTypes = [];
    public selectedDocumentType: IDocumentTypesDto | null;
    public advancedFiltersList$: Observable<IAdvancedFilterListItem<AdvanceFilterParamType>[]> = this._documentListingFacade.advancedFiltersList$;
    public isAdvancedFiltersListLoading$: Observable<boolean> = this._documentListingFacade.isAdvancedFiltersListLoading$;
    public appliedAdvancedFilters$: Observable<IAdvanceFilter<AdvanceFilterParamType>[]> = this._documentListingFacade.appliedAdvancedFilters$.pipe(map(x => ([...x])));
    public readonly = false;
    public searchable = false;
    public ownerSearch$ = this._documentListingFacade.ownerSearch$;
    public perPageRecords: number = 20;
    
    private readonly _destroyed$ = new Subject<void>();
    private readonly _ownerSearchChange$: Subject<string> = new Subject<string>();
    private readonly _debounceTime: number = 500;


    ngOnInit(): void {
        this.documentTypes$.pipe(
            filter(result => result?.length > 0),
            take(1)
        ).subscribe((response) => {
            this.documentTypes = [{ id: 0, document_type: 'All' }, ...response];
        });

        this._documentListingFacade.fetchFilterData();
        this._documentListingFacade.fetchAdvancedFilters();

        this._ownerSearchChange$
            .pipe(
                takeUntil(this._destroyed$),
                debounceTime(this._debounceTime)
            )
            .subscribe(searchValue => this._documentListingFacade.onOwnerSearchChange(searchValue))
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    constructor(private _documentListingFacade: DocumentListingFacade,
        private modalService: NgbModal,
        private datePipe: DatePipe) {
    }

    setPerPageRecords(value: number) {
        this.perPageRecords = value;
        this._documentListingFacade.setPerPageRecords(value);
    }

    showNestedCharts(view: DocumentViewVariantEnum) {
        this.showNestedChartViews.emit({ view: view });
    }

    onDateFromChange(date: Date) {
        this._documentListingFacade.filterByFromDate(this.datePipe.transform(date, this.dateFormat));
    }

    onDateTomChange(date: Date) {
        this._documentListingFacade.filterByToDate(this.datePipe.transform(date, this.dateFormat));
    }

    onDocumentTypeFilterChange(documentType: IDocumentTypesDto | number | null | Event) {
        if (documentType instanceof Event) {
            // ng-select emit change on both html events and model change. Since we want to make only actions on model change we have to filter out html events
            return;
        }

        const selectedDocumentType = typeof documentType === 'number'
            ? this.documentTypes.find(item => item.id === documentType)
            : documentType;
        const selectedDocumentTypeId = selectedDocumentType?.id ?? 0;

        const columns = selectedDocumentType?.sections?.find(x => x.name === DocumentTypeSectionEnum.DocumentColumns);
        if (selectedDocumentTypeId && columns?.columns?.length) {
            const columnSettings = columns.columns.map(field => ({
                uuid: String(field.id),
                field_name: field.field_name,
                label: field.label,
                order: field.order,
                doc_id: field.meta_data_id,
                status: field.status ? 1 : 0,
                type: field.type,
                details: field.meta_data_id ? field.meta_data : null
            }));
            this._documentListingFacade.setCustomColumns(columnSettings);
        } else {
            this._documentListingFacade.fetchColumnSettings();
        }

        this.selectedDocumentType = (selectedDocumentType as IDocumentTypesDto | null) ?? null;
        this._documentListingFacade.filterByDocumentType(selectedDocumentTypeId);
    }

    onUserFilterChange(id: IUserGroupFilter) {
        this._documentListingFacade.filterByUser(id);
    }

    advancedFilterSearch($event: string) {
        this._documentListingFacade.advancedFilterSearch($event);
    }

    addNewFilter(filter: Omit<IAdvanceFilter<AdvanceFilterParamType>, 'uuid'>) {
        this._documentListingFacade.addNewFilter(filter);
    }

    onRemoveFilter(filter: IAdvanceFilter<AdvanceFilterParamType>) {
        this._documentListingFacade.onRemoveFilter(filter);
    }

    onSelectedSavedFilterChange(filterId: number) {
        this._documentListingFacade.restoreFilter(filterId);
    }

    // old onDocumentCategoryChange removed/merged above



    onSaveFilter() {
        const instance = this.modalService.open(SaveFilterComponent, {
            size: 'm'
        });

        (instance.componentInstance as SaveFilterComponent).saveFilter
            .subscribe(x => {
                (instance.componentInstance as SaveFilterComponent).isLoading = true;
                this._documentListingFacade.saveFilter(x);
                instance.close();
            });
    }

    filterOwner(search: string) {
        this._ownerSearchChange$.next(search);
    }

    compareOwnerFn(ownerA: IUserGroupFilter, ownerB: IUserGroupFilter) {
        return ownerA.id === ownerB.id && ownerA.group === ownerB.group;
    }

    // Category Tree Dropdown Methods
    onDocumentCategoryChange(id: number) {
        this._documentListingFacade.filterByCategory(id);
    }

    protected readonly OwnerFilterType = OwnerFilterType;
}


